import { DropDownButton, DropDownButtonItem, DropDownButtonItemClickEvent } from '@progress/kendo-react-buttons';
import React from 'react';
import { gearIcon, gearsIcon, logoutIcon, infoSolidIcon, globeIcon, paletteIcon } from '@progress/kendo-svg-icons';
import { AboutDialog } from './AboutDialog';
import { useNavigate } from 'react-router-dom';
import { GearLanguage, useGlobalizationContext, useTranslation } from '@cs-cms/state-management';
import { CpsLocale, THEME } from '@cs-cms/types';
import { useUIConfig } from '../UiProvider';
import { RoutePath } from '../types/RoutePath';

const SettingsButton: React.FC = () => {
  const navigate = useNavigate();
  const [isAboutDialogOpen, setIsAboutDialogOpen] = React.useState(false);
  const { selectedLocale, setSelectedLocale, availableLocales } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { theme, toggleTheme } = useUIConfig();

  const settingsText = translate('custom.settings.title');
  const logoutText = translate('custom.settings.logout');
  const aboutText = translate('custom.settings.about');
  const languageText = translate('custom.topBar.language.title');

  const themeText = translate('custom.settings.theme');
  const lightModeText = translate('custom.settings.lightMode');
  const darkModeText = translate('custom.settings.darkMode');

  const languagesDictionary: GearLanguage[] | undefined = availableLocales?.map(availableLocale => ({
    locale: availableLocale.name as CpsLocale,
    text: translate(`custom.topBar.language.${availableLocale.name}`)
  }));

  const handleItemClicked = (event: DropDownButtonItemClickEvent) => {
    switch (event.item.text) {
      case settingsText:
        navigate(`/${RoutePath.SETTINGS}/${RoutePath.SETTINGS_VIEWS}`);
        break;
      case logoutText:
        navigate(RoutePath.LOGOUT);
        break;
      case aboutText:
        setIsAboutDialogOpen(true);
        break;
      case lightModeText:
      case darkModeText:
        toggleTheme();
        break;
      default: {
        const selectedLanguage = languagesDictionary?.find(lang => lang.text === event.item.text);
        if (setSelectedLocale && selectedLanguage) {
          setSelectedLocale(selectedLanguage.locale);
        }
        break;
      }
    }
  };

  const dropdownItems = [
    <DropDownButtonItem key="settings" svgIcon={gearsIcon} text={settingsText} />,
    <DropDownButtonItem key="theme-header" disabled={true} svgIcon={paletteIcon} text={themeText} />,
    <DropDownButtonItem
      key="light-mode"
      text={lightModeText}
      selected={theme === THEME.LIGHT}
      disabled={theme === THEME.LIGHT}
    />,
    <DropDownButtonItem
      key="dark-mode"
      text={darkModeText}
      selected={theme === THEME.DARK}
      disabled={theme === THEME.DARK}
    />,
    ...(languagesDictionary && languagesDictionary.length > 0
      ? [
          <DropDownButtonItem key="lang-header" disabled={true} svgIcon={globeIcon} text={languageText} />,
          ...languagesDictionary.map((language, index) => (
            <DropDownButtonItem
              key={`lang-${index}`}
              text={language.text}
              icon={language.icon}
              selected={language.locale === selectedLocale}
            />
          ))
        ]
      : []),
    <DropDownButtonItem key="about" svgIcon={infoSolidIcon} text={aboutText} />,
    <DropDownButtonItem key="logout" svgIcon={logoutIcon} text={logoutText} />
  ];

  return (
    <>
      <DropDownButton
        svgIcon={gearIcon}
        onItemClick={handleItemClicked}
        fillMode={'solid'}
        size={'medium'}
        themeColor={'primary'}
      >
        {dropdownItems}
      </DropDownButton>

      <AboutDialog isOpen={isAboutDialogOpen} onClose={() => setIsAboutDialogOpen(false)} />
    </>
  );
};

export default SettingsButton;
